import type { CarpetPricingCatalogItem } from '@jobos/shared';
import { Button } from '@/components/ui/button';

interface ServiceTierPickerProps {
  items: CarpetPricingCatalogItem[];
  value: string | null;
  onChange: (key: string | null) => void;
  readOnly?: boolean;
}

const formatRate = (rate: number | string): string =>
  `$${Number(rate).toFixed(0)}`;

export function ServiceTierPicker({ items, value, onChange, readOnly = false }: ServiceTierPickerProps) {
  if (items.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No service tiers configured. Add one in Settings → Carpet Cleaning Rate Card.
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {items.map((item) => {
        const selected = value === item.key;
        const hidden = readOnly && !selected;
        if (hidden) return null;
        return (
          <button
            key={item.key}
            type="button"
            aria-pressed={selected}
            disabled={readOnly}
            onClick={() => onChange(selected ? null : item.key)}
            className={[
              'rounded-full border px-4 py-2 text-sm transition-colors',
              selected
                ? 'border-blue-600 bg-blue-50 text-blue-900'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
              readOnly ? 'cursor-default opacity-80' : '',
            ].join(' ')}
          >
            <span className="font-medium">{item.label}</span>
            <span className="ml-2 text-gray-500">{formatRate(item.defaultRate)}</span>
          </button>
        );
      })}
      {value && !readOnly && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange(null)}
        >
          Clear
        </Button>
      )}
    </div>
  );
}
